import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronRight, Edit, Trash2, AlertTriangle, Loader, Plus, Pill, X, FileText } from 'lucide-react';
import ApiService from './ApiService';
import MedicationAssignment from './MedicationAssignment';
import ProtocolAssignment from './ProtocolAssignment';
import ServiceAssignment from './ServiceAssignment';
import PatientNotes from './PatientNotes';
import PatientDocumentsSection from './PatientDocumentsSection';
import PatientHistorySection from './PatientHistorySection';
import { MedicationAuditInfo, ServiceAuditInfo, ProtocolAuditInfo } from './AuditTrailComponents';
import { usePermissions, PERMISSIONS } from './usePermissions';
import AccessDenied from './AccessDenied';

// Format date from YYYY-MM-DD to MM-DD-YYYY for display
const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${month}-${day}-${year}`;
};

// Patient Edit Modal Component
const PatientEditModal = ({ isOpen, onClose, patient, config, onPatientUpdated }) => {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        cellphoneProvider: '',
        dateOfBirth: '',
        notes: '',
        preferSMS: false,
        preferEmail: true
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const t = config?.terminology || {
        client: { singular: 'Patient', singularLower: 'patient' }
    };

    useEffect(() => {
        if (isOpen && patient) {
            // Split name if it exists
            const nameParts = patient.name ? patient.name.split(' ') : ['', ''];
            const firstName = patient.firstName || nameParts[0] || '';
            const lastName = patient.lastName || nameParts.slice(1).join(' ') || '';

            setFormData({
                firstName,
                lastName,
                email: patient.email || '',
                phone: patient.phone || '',
                cellphoneProvider: patient.cellphoneProvider || '',
                dateOfBirth: patient.dateOfBirth || '',
                notes: patient.notes || '',
                preferSMS: patient.preferSMS || false,
                preferEmail: patient.preferEmail !== undefined ? patient.preferEmail : true
            });
        }
    }, [isOpen, patient]);

    const handleNameChange = (field, value) => {
        const lettersOnly = value.replace(/[^a-zA-Z\s-]/g, '');
        setFormData({ ...formData, [field]: lettersOnly });
    };

    const handlePhoneChange = (value) => {
        const numbers = value.replace(/\D/g, '');
        const limited = numbers.slice(0, 10);
        let formatted = limited;
        if (limited.length >= 6) {
            formatted = `${limited.slice(0, 3)}-${limited.slice(3, 5)}-${limited.slice(5)}`;
        } else if (limited.length >= 3) {
            formatted = `${limited.slice(0, 3)}-${limited.slice(3)}`;
        }
        setFormData({ ...formData, phone: formatted });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSaving(true);

        try {
            // Combine firstName and lastName into name for API
            const updateData = {
                ...formData,
                name: `${formData.firstName} ${formData.lastName}`.trim()
            };

            const updatedPatient = await ApiService.updatePatient(patient.patientId, updateData);
            onPatientUpdated(updatedPatient);
            onClose();
        } catch (err) {
            setError('Failed to update patient');
            console.error(err);
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-2xl font-bold text-gray-800">Edit {t.client.singular}</h2>
                        <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                            <X size={24} />
                        </button>
                    </div>

                    {error && (
                        <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg">
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* First Name */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                First Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={formData.firstName}
                                onChange={(e) => handleNameChange('firstName', e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500"
                                required
                            />
                        </div>

                        {/* Last Name */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Last Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={formData.lastName}
                                onChange={(e) => handleNameChange('lastName', e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500"
                                required
                            />
                        </div>

                        {/* Email */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Email <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500"
                                required
                            />
                        </div>

                        {/* Phone */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Phone Number
                            </label>
                            <input
                                type="text"
                                value={formData.phone}
                                onChange={(e) => handlePhoneChange(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500"
                                placeholder="123-45-6789"
                                maxLength="12"
                            />
                            <p className="text-xs text-gray-500 mt-1">Format: XXX-XX-XXXX</p>
                        </div>

                        {/* Cellphone Provider */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Cellphone Provider
                            </label>
                            <select
                                value={formData.cellphoneProvider}
                                onChange={(e) => setFormData({ ...formData, cellphoneProvider: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500"
                            >
                                <option value="">Select Provider</option>
                                <option value="Verizon">Verizon</option>
                                <option value="AT&T">AT&T</option>
                                <option value="T-Mobile">T-Mobile</option>
                                <option value="Mint">Mint</option>
                            </select>
                        </div>

                        {/* Timezone */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Timezone
                            </label>
                            <select
                                value={formData.timezone}
                                onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500"
                            >
                                <option value="">Clinic default</option>
                                <option value="America/New_York">Eastern (New York)</option>
                                <option value="America/Chicago">Central (Chicago)</option>
                                <option value="America/Denver">Mountain (Denver)</option>
                                <option value="America/Phoenix">Arizona (Phoenix)</option>
                                <option value="America/Los_Angeles">Pacific (Los Angeles)</option>
                                <option value="America/Anchorage">Alaska</option>
                                <option value="Pacific/Honolulu">Hawaii</option>
                            </select>
                            <p className="text-xs text-gray-500 mt-1">Reminders send on the patient's local time</p>
                        </div>

                        {/* Date of Birth */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Date of Birth
                            </label>
                            <input
                                type="date"
                                value={formData.dateOfBirth}
                                onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500"
                            />
                        </div>

                        {/* Reminder Preferences */}
                        <div className="space-y-3">
                            <label className="block text-sm font-semibold text-gray-700">
                                Reminder Preferences
                            </label>
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="preferSMS"
                                    checked={formData.preferSMS}
                                    onChange={(e) => setFormData({ ...formData, preferSMS: e.target.checked })}
                                    className="w-4 h-4 text-spruce-600 rounded focus:ring-spruce-500"
                                />
                                <label htmlFor="preferSMS" className="ml-2 text-sm text-gray-700">
                                    Prefer SMS Text Message Reminders
                                </label>
                            </div>
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="preferEmail"
                                    checked={formData.preferEmail}
                                    onChange={(e) => setFormData({ ...formData, preferEmail: e.target.checked })}
                                    className="w-4 h-4 text-spruce-600 rounded focus:ring-spruce-500"
                                />
                                <label htmlFor="preferEmail" className="ml-2 text-sm text-gray-700">
                                    Prefer Email Reminders
                                </label>
                            </div>
                        </div>

                        {/* Notes */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Notes
                            </label>
                            <textarea
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500"
                                rows="3"
                                placeholder="Additional notes..."
                            />
                        </div>

                        {/* Buttons */}
                        <div className="flex gap-3 justify-end pt-4">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={saving}
                                className="px-6 py-2 bg-spruce-600 text-white rounded-lg hover:bg-spruce-700 font-medium disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                {saving ? (
                                    <>
                                        <Loader className="w-4 h-4 animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    'Save Changes'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

const PatientDetailPage = ({ patient: initialPatient, config, onBack, onPatientDeleted, currentUser }) => {
    const [patient, setPatient] = useState(initialPatient);
    // Deep-link support: the Protocols Overview links here with ?tab=protocols&highlight=<id>.
    const [searchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'medications');
    const [highlightId, setHighlightId] = useState(searchParams.get('highlight') || null);
    const [loading, setLoading] = useState(true);
    const [protocols, setProtocols] = useState([]);
    const [medications, setMedications] = useState([]);
    const [services, setServices] = useState([]);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [errorModal, setErrorModal] = useState(null);
    const [showMedicationAssignment, setShowMedicationAssignment] = useState(false);
    const [showProtocolAssignment, setShowProtocolAssignment] = useState(false);
    const [showServiceAssignment, setShowServiceAssignment] = useState(false);
    const [selectedProtocol, setSelectedProtocol] = useState(null);
    const [showProtocolDeleteConfirm, setShowProtocolDeleteConfirm] = useState(false);
    const [protocolToDelete, setProtocolToDelete] = useState(null);
    const [showMedicationDeleteConfirm, setShowMedicationDeleteConfirm] = useState(false);
    const [medicationToDelete, setMedicationToDelete] = useState(null);
    const [showServiceDeleteConfirm, setShowServiceDeleteConfirm] = useState(false);
    const [serviceToDelete, setServiceToDelete] = useState(null);
    const [notes, setNotes] = useState([]);
    const { hasPermission } = usePermissions(currentUser);
    const t = config?.terminology || {
        item: { singular: 'Medication', plural: 'Medications', singularLower: 'medication', pluralLower: 'medications' },
        protocol: { singular: 'Protocol', plural: 'Protocols', singularLower: 'protocol', pluralLower: 'protocols' },
        service: { singular: 'Service', plural: 'Services', singularLower: 'service', pluralLower: 'services' },
        client: { singular: 'Patient', plural: 'Patients', singularLower: 'patient', pluralLower: 'patients' },
        note: { singular: 'Note', plural: 'Notes', singularLower: 'note', pluralLower: 'notes' },
        document: { singular: 'Document', plural: 'Documents', singularLower: 'document', pluralLower: 'documents' }
    };

    const loadPatientData = useCallback(async () => {
        try {
            setLoading(true);

            // Update lastAccessedAt timestamp
            ApiService.touchPatient(patient.patientId).catch(err =>
                console.error('Failed to touch patient:', err)
            );

            const [protocolsData, medicationsData, servicesData, notesData] = await Promise.all([
                ApiService.getPatientProtocols(patient.patientId),
                ApiService.getPatientMedications(patient.patientId),
                ApiService.getPatientServices(patient.patientId),
                ApiService.getPatientNotes(patient.patientId)
            ]);

            setProtocols(protocolsData);
            setMedications(medicationsData);
            setServices(servicesData);
            setNotes(notesData);
        } catch (err) {
            console.error('Failed to load patient data:', err);
        } finally {
            setLoading(false);
        }
    }, [patient.patientId]);

    useEffect(() => {
        loadPatientData();
    }, [loadPatientData]);

    // Deep-link: once protocols are loaded, scroll to + briefly highlight the one
    // named in ?highlight= (from the Protocols Overview page).
    useEffect(() => {
        if (!highlightId || activeTab !== 'protocols' || protocols.length === 0) return;
        const el = document.getElementById(`protocol-${highlightId}`);
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-2', 'ring-copper-400', 'ring-offset-2');
        const timer = setTimeout(() => {
            el.classList.remove('ring-2', 'ring-copper-400', 'ring-offset-2');
            setHighlightId(null);
        }, 2500);
        return () => clearTimeout(timer);
    }, [highlightId, activeTab, protocols]);

    const handlePatientUpdated = (updatedPatient) => {
        setPatient(updatedPatient);
    };

    const handleDeletePatient = async () => {
        setDeleting(true);
        try {
            await ApiService.deletePatient(patient.patientId);
            onPatientDeleted(patient.patientId);
            onBack();
        } catch (err) {
            console.error('Failed to delete patient:', err);
            setErrorModal({ title: 'Error Deleting Patient', message: err.message || 'Failed to delete patient. Please try again.' });
        } finally {
            setDeleting(false);
            setShowDeleteConfirm(false);
        }
    };

    // Simple assignments render as plain medication line-items; anything the
    // protocol builder has shaped shows the full schedule block.
    const isSimpleAssignment = (protocol) => {
        if (protocol.assignmentStyle === 'simple') return true;
        if (protocol.assignmentStyle === 'protocol') return false;
        // Heuristic for records created before the flag existed
        const s = protocol.schedule || {};
        if (protocol.protocolType === 'as-needed') return true;
        if (protocol.protocolType === 'daily' && (s.doses?.length || 0) <= 1 && !s.phases) return true;
        if (protocol.protocolType === 'weekly' && !s.timesPerWeek) return true;
        return false;
    };

    const simpleSummary = (protocol) => {
        const s = protocol.schedule || {};
        const dose = protocol.protocolType === 'daily' ? (s.doses?.[0] || {}) : s;
        const amount = dose.dosage ? `${dose.dosage}${dose.dosageUnit ? ' ' + dose.dosageUnit : ''}` : null;
        const freq = protocol.protocolType === 'as-needed' ? 'As needed'
            : protocol.protocolType === 'weekly'
                ? `Weekly${s.selectedDays?.length ? ' - ' + s.selectedDays.join(', ') : ''}`
                : 'Daily';
        return { amount, freq, instructions: dose.instructions || s.instructions || '' };
    };

    const handleMedicationAssigned = () => {
        // Reload patient data after assigning medication
        loadPatientData();
    };

    const handleProtocolAssigned = () => {
        // Reload patient data after creating protocol
        loadPatientData();
    };

    const handleServiceAssigned = () => {
        // Reload patient data after adding service
        loadPatientData();
    };

    // Check if patient has any medications assigned
    // const hasMedications = medications.length > 0;

    if (!patient) return null;

    return (
        <div className="space-y-6">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm text-gray-500">
                <button onClick={onBack} className="hover:text-spruce-600">
                    {t.client.plural}
                </button>
                <ChevronRight size={16} />
                <span>{patient.name}</span>
            </div>

            {/* Patient Header */}
            <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex justify-between items-start">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-800">{patient.name}</h2>
                        <div className="mt-2 space-y-1 text-gray-600">
                            <p>{patient.email}</p>
                            {patient.phone && <p>{patient.phone}</p>}
                            {patient.dateOfBirth && (
                                <p>🎂 Date of Birth: {formatDateForDisplay(patient.dateOfBirth)}</p>
                            )}
                        </div>
                        {patient.notes && (
                            <div className="mt-3 p-3 bg-spruce-50 border border-spruce-200 rounded">
                                <p className="text-sm text-spruce-900">
                                    <strong>Notes:</strong> {patient.notes}
                                </p>
                            </div>
                        )}
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                        {hasPermission(PERMISSIONS.PATIENTS_EDIT) && (
                            <button
                                onClick={() => setShowEditModal(true)}
                                className="flex items-center gap-2 px-4 py-2 text-spruce-600 hover:bg-spruce-50 rounded-lg transition"
                            >
                                <Edit size={18} />
                                Edit
                            </button>
                        )}
                        {hasPermission(PERMISSIONS.PATIENTS_DELETE) && (
                            <button
                                onClick={() => setShowDeleteConfirm(true)}
                                className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                            >
                                <Trash2 size={18} />
                                Delete
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-3 bg-red-100 rounded-full">
                                <AlertTriangle className="text-red-600" size={24} />
                            </div>
                            <h3 className="text-xl font-bold text-gray-800">Delete {t.client.singular}?</h3>
                        </div>
                        <p className="text-gray-600 mb-4">
                            Are you sure you want to delete <strong>{patient.name}</strong>?
                        </p>
                        <p className="text-sm text-gray-600 mb-4">
                            The patient will be moved to the <strong>Recycle Bin</strong> along with all their
                            medications, protocols, services, notes, and documents. They will no longer appear
                            in the directory or receive any reminders.
                        </p>
                        <p className="text-sm text-spruce-700 mb-6">
                            An administrator can restore them — or delete them permanently — from
                            Administration → Recycle Bin.
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={handleDeletePatient}
                                disabled={deleting}
                                className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 disabled:opacity-50 transition flex items-center justify-center gap-2"
                            >
                                {deleting ? (
                                    <>
                                        <Loader className="animate-spin" size={18} />
                                        Deleting...
                                    </>
                                ) : (
                                    <>
                                        <Trash2 size={18} />
                                        Yes, Delete
                                    </>
                                )}
                            </button>
                            <button
                                onClick={() => setShowDeleteConfirm(false)}
                                disabled={deleting}
                                className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 disabled:opacity-50 transition"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Tabs */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="border-b border-gray-200">
                    <nav className="flex overflow-x-auto -mb-px scrollbar-hide">
                        <button
                            onClick={() => setActiveTab(t.item.pluralLower)}
                            className={`px-6 py-3 text-sm font-medium border-b-2 transition ${activeTab === t.item.pluralLower
                                ? 'border-spruce-500 text-spruce-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                        >
                            {t.item.plural}
                        </button>
                        <button
                            onClick={() => setActiveTab('protocols')}
                            className={`px-6 py-3 text-sm font-medium border-b-2 transition ${activeTab === 'protocols'
                                ? 'border-spruce-500 text-spruce-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                        >
                            {t.protocol.plural}
                        </button>
                        <button
                            onClick={() => setActiveTab(t.service.pluralLower)}
                            className={`px-6 py-3 text-sm font-medium border-b-2 transition ${activeTab === t.service.pluralLower
                                ? 'border-spruce-500 text-spruce-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                        >
                            {t.service.plural}
                        </button>
                        <button
                            onClick={() => setActiveTab(t.note.pluralLower)}
                            className={`px-6 py-3 text-sm font-medium border-b-2 transition ${activeTab === t.note.pluralLower
                                ? 'border-spruce-500 text-spruce-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                        >
                            {t.note.plural}
                        </button>
                        <button
                            onClick={() => setActiveTab(t.document.pluralLower)}
                            className={`px-6 py-3 text-sm font-medium border-b-2 transition ${activeTab === t.document.pluralLower
                                ? 'border-spruce-500 text-spruce-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                        >
                            {t.document.plural}
                        </button>
                        <button
                            onClick={() => setActiveTab('history')}
                            className={`px-6 py-3 text-sm font-medium border-b-2 transition ${activeTab === 'history'
                                ? 'border-spruce-500 text-spruce-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                        >
                            History
                        </button>
                    </nav>
                </div>

                <div className="p-6">
                    {loading ? (
                        <div className="text-center py-12 text-gray-500">Loading...</div>
                    ) : (
                        <>
                            {/* Medications Tab */}
                            {/* Medications: what the patient is on. Bare chart entries -
                                dosage/schedule/reminders come from Protocols. */}
                            {activeTab === 'medications' && (
                                <>
                                    {!hasPermission(PERMISSIONS.PATIENTS_PROTOCOLS_VIEW) ? (
                                        <AccessDenied resource="medications" action="view" />
                                    ) : (
                                        <div className="space-y-4">
                                            <div className="flex justify-between items-center">
                                                <h3 className="text-lg font-semibold text-gray-800">Assigned {t.item.plural}</h3>
                                                {hasPermission(PERMISSIONS.PATIENTS_PROTOCOLS_ADD) && (
                                                    <button
                                                        onClick={() => setShowMedicationAssignment(true)}
                                                        className="bg-spruce-600 text-white px-4 py-2 rounded-lg hover:bg-spruce-700 flex items-center gap-2"
                                                    >
                                                        <Plus size={18} />
                                                        Add {t.item.singular}
                                                    </button>
                                                )}
                                            </div>
                                            {protocols.filter(isSimpleAssignment).length === 0 ? (
                                                <div className="text-center py-12 text-gray-500">
                                                    No {t.item.pluralLower} assigned yet
                                                </div>
                                            ) : (
                                                <div className="space-y-3">
                                                    {protocols
                                                        .filter(isSimpleAssignment)
                                                        .sort((a, b) => (a.medicationName || '').localeCompare(b.medicationName || ''))
                                                        .map((assignment) => (
                                                            <div
                                                                key={assignment.patientProtocolId}
                                                                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition"
                                                            >
                                                                <div className="flex justify-between items-start">
                                                                    <div className="flex-1">
                                                                        <h4 className="font-semibold text-gray-800">{assignment.medicationName}</h4>
                                                                        <div className="flex gap-3 mt-1.5 items-center text-sm text-gray-600">
                                                                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${assignment.status === 'active'
                                                                                ? 'bg-green-100 text-green-800'
                                                                                : 'bg-gray-100 text-gray-600'}`}>
                                                                                {assignment.status}
                                                                            </span>
                                                                            <span className="text-xs text-gray-500">Assigned {formatDateForDisplay(assignment.startDate)}</span>
                                                                        </div>
                                                                        {assignment.notes && (
                                                                            <div className="text-sm text-gray-600 mt-2">{assignment.notes}</div>
                                                                        )}
                                                                        {!protocols.some(p => !isSimpleAssignment(p) && p.medicationId === assignment.medicationId) && (
                                                                            <div className="text-xs text-copper-600 mt-2">
                                                                                No schedule yet - create a {t.protocol.singular.toLowerCase()} for this {t.item.singular.toLowerCase()} to enable reminders
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    {hasPermission(PERMISSIONS.PATIENTS_PROTOCOLS_DELETE) && (
                                                                        <button
                                                                            onClick={() => {
                                                                                setProtocolToDelete(assignment);
                                                                                setShowProtocolDeleteConfirm(true);
                                                                            }}
                                                                            className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                                                                            title="Remove medication"
                                                                        >
                                                                            <Trash2 size={18} />
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}

                            {/* Protocols: schedules built for assigned medications */}
                            {activeTab === 'protocols' && (
                                <>
                                    {!hasPermission(PERMISSIONS.PATIENTS_PROTOCOLS_VIEW) ? (
                                        <AccessDenied resource="protocols" action="view" />
                                    ) : (
                                        <div className="space-y-4">
                                    <div className="flex justify-between items-center">
                                        <h3 className="text-lg font-semibold text-gray-800">Assigned {t.protocol.plural}</h3>
                                        {hasPermission(PERMISSIONS.PATIENTS_PROTOCOLS_ADD) && (
                                            <button
                                                onClick={() => setShowProtocolAssignment(true)}
                                                className="bg-spruce-600 text-white px-4 py-2 rounded-lg hover:bg-spruce-700 flex items-center gap-2"
                                            >
                                                <Plus size={18} />
                                                Create {t.protocol.singular}
                                            </button>
                                        )}
                                    </div>
                                    {protocols.filter(p => !isSimpleAssignment(p)).length === 0 ? (
                                        <div className="text-center py-12 text-gray-500">
                                            No {t.protocol.pluralLower} created yet - assign a {t.item.singular.toLowerCase()} first, then create a {t.protocol.singular.toLowerCase()} for it here
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            {protocols
                                                .filter(p => !isSimpleAssignment(p))
                                                .sort((a, b) => (a.protocolName || '').localeCompare(b.protocolName || ''))
                                                .map((protocol) => (
                                                    <div
                                                        key={protocol.patientProtocolId}
                                                        id={`protocol-${protocol.patientProtocolId}`}
                                                        className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition"
                                                    >
                                                        <div className="flex justify-between items-start">
                                                            <div className="flex-1">
                                                                <h4 className="font-semibold text-gray-800">{protocol.protocolName}</h4>
                                                                <div className="text-sm text-gray-600 mt-1">
                                                                    <span className="font-medium">{protocol.medicationName}</span>
                                                                </div>
                                                                <div className="flex gap-4 mt-2 text-sm text-gray-600">
                                                                    {protocol.currentPhase && <span>Phase {protocol.currentPhase}</span>}
                                                                    <span>Started: {formatDateForDisplay(protocol.startDate)}</span>
                                                                    <span>End by: {protocol.endDate ? formatDateForDisplay(protocol.endDate) : 'Ongoing'}</span>
                                                                    {protocol.completedAt && <span>Ended: {formatDateForDisplay(protocol.completedAt)}</span>}
                                                                    <span
                                                                        className={`px-2 py-1 rounded-full text-xs font-medium ${protocol.status === 'active'
                                                                            ? 'bg-green-100 text-green-800'
                                                                            : protocol.status === 'completed'
                                                                                ? 'bg-spruce-100 text-spruce-800'
                                                                                : 'bg-gray-100 text-gray-800'
                                                                            }`}
                                                                    >
                                                                        {protocol.status}
                                                                    </span>
                                                                </div>
                                                                {protocol.notes && (
                                                                    <p className="mt-2 text-sm text-gray-600">{protocol.notes}</p>
                                                                )}

                                                                {/* Protocol Details */}
                                                                {protocol.schedule && (
                                                                    <div className="mt-3 pt-3 border-t border-gray-100">
                                                                        <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Schedule</div>
                                                                        {protocol.protocolType === 'daily' && protocol.schedule.doses && (
                                                                            <div className="space-y-1">
                                                                                {protocol.schedule.doses.map((dose, idx) => (
                                                                                    <div key={idx} className="text-sm text-gray-700">
                                                                                        • {dose.dosage}{dose.dosageUnit} at {dose.time}
                                                                                        {dose.instructions && ` - ${dose.instructions}`}
                                                                                    </div>
                                                                                ))}
                                                                                {protocol.schedule.duration && (
                                                                                    <div className="text-xs text-gray-500 mt-1">
                                                                                        Duration: {protocol.schedule.duration} {protocol.schedule.durationUnit}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        )}

                                                                        {protocol.protocolType === 'as-needed' && (
                                                                            <div className="text-sm text-gray-700">
                                                                                • {protocol.schedule.dosage}{protocol.schedule.dosageUnit ? ` ${protocol.schedule.dosageUnit}` : ''} - as needed
                                                                                {protocol.schedule.instructions && (
                                                                                    <div className="text-xs text-gray-600">{protocol.schedule.instructions}</div>
                                                                                )}
                                                                            </div>
                                                                        )}

                                                                        {protocol.protocolType === 'weekly' && (
                                                                            <div className="text-sm text-gray-700">
                                                                                • {protocol.schedule.dosage}{protocol.schedule.dosageUnit ? ` ${protocol.schedule.dosageUnit}` : ''}{protocol.schedule.timesPerWeek ? ` - ${protocol.schedule.timesPerWeek} time(s) per week` : ' - weekly'}
                                                                                {protocol.schedule.specificDays && protocol.schedule.selectedDays?.length > 0 && (
                                                                                    <div className="text-xs text-gray-600 mt-1">
                                                                                        Days: {protocol.schedule.selectedDays.join(', ')}
                                                                                    </div>
                                                                                )}
                                                                                {protocol.schedule.instructions && (
                                                                                    <div className="text-xs text-gray-600">{protocol.schedule.instructions}</div>
                                                                                )}
                                                                            </div>
                                                                        )}

                                                                        {protocol.protocolType === 'multiphase' && protocol.schedule.phases && (
                                                                            <div className="space-y-2">
                                                                                {protocol.schedule.phases.map((phase, idx) => (
                                                                                    <div key={idx} className="text-sm">
                                                                                        <span className="font-medium text-gray-700">Phase {phase.phaseNumber}:</span>
                                                                                        <span className="text-gray-600 ml-2">
                                                                                            {phase.dosage}{phase.dosageUnit} -
                                                                                            {phase.frequency === 'daily' ? ` ${phase.timesPerDay}x daily` : ` ${phase.timesPerWeek}x weekly`}
                                                                                            {phase.duration && ` for ${phase.duration} ${phase.durationUnit}`}
                                                                                        </span>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}
                                                                <ProtocolAuditInfo protocol={protocol} />
                                                            </div>

                                                            <div className="flex gap-2 ml-4">
                                                                <button
                                                                    onClick={() => {
                                                                        setSelectedProtocol(protocol);
                                                                        setShowProtocolAssignment(true);
                                                                    }}
                                                                    className="p-2 text-spruce-600 hover:bg-spruce-50 rounded transition-colors"
                                                                    title="Edit protocol"
                                                                >
                                                                    <Edit size={18} />
                                                                </button>

                                                                <button
                                                                    onClick={() => {
                                                                        setProtocolToDelete(protocol);
                                                                        setShowProtocolDeleteConfirm(true);
                                                                    }}
                                                                    className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                                                                    title="Delete protocol"
                                                                >
                                                                    <Trash2 size={18} />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                        </div>
                                    )}
                                        </div>
                                    )}
                                </>
                            )}

                            {/* Services Tab */}
                            {activeTab === 'services' && (
                                <>
                                    {!hasPermission(PERMISSIONS.PATIENTS_SERVICES_VIEW) ? (
                                        <AccessDenied resource="services" action="view" />
                                    ) : (
                                        <div className="space-y-4">
                                    <div className="flex justify-between items-center">
                                        <h3 className="text-lg font-semibold text-gray-800">Scheduled {t.service.plural}</h3>
                                        {hasPermission(PERMISSIONS.PATIENTS_SERVICES_ADD) && (
                                            <button
                                                onClick={() => setShowServiceAssignment(true)}
                                                className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 flex items-center gap-2"
                                            >
                                                <Plus size={18} />
                                                Add {t.service.singular}
                                            </button>
                                        )}
                                    </div>
                                    {services.length === 0 ? (
                                        <div className="text-center py-12 text-gray-500">
                                            No {t.service.pluralLower} scheduled yet
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            {services
                                                .sort((a, b) => (a.serviceName || '').localeCompare(b.serviceName || ''))
                                                .map((service) => (
                                                    <div
                                                        key={service.patientServiceId}
                                                        className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition"
                                                    >
                                                        <div className="flex justify-between items-start">
                                                            <div className="flex-1">
                                                                <h4 className="font-semibold text-gray-800">{service.serviceName}</h4>
                                                                <div className="flex gap-4 mt-2 text-sm text-gray-600">
                                                                    <span>Due: {formatDateForDisplay(service.nextDueDate)}</span>
                                                                    {service.lastCompletedDate && (
                                                                        <span>✓ Last: {formatDateForDisplay(service.lastCompletedDate)}</span>
                                                                    )}
                                                                    <span
                                                                        className={`px-2 py-1 rounded-full text-xs font-medium ${service.status === 'scheduled'
                                                                            ? 'bg-orange-100 text-orange-800'
                                                                            : service.status === 'completed'
                                                                                ? 'bg-green-100 text-green-800'
                                                                                : 'bg-gray-100 text-gray-800'
                                                                            }`}
                                                                    >
                                                                        {service.status}
                                                                    </span>
                                                                </div>
                                                                {service.notes && (
                                                                    <p className="mt-2 text-sm text-gray-600">{service.notes}</p>
                                                                )}
                                                                <ServiceAuditInfo service={service} />
                                                            </div>

                                                            <button
                                                                onClick={() => {
                                                                    setServiceToDelete(service);
                                                                    setShowServiceDeleteConfirm(true);
                                                                }}
                                                                className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                                                                title="Delete service"
                                                            >
                                                                <Trash2 size={18} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                        </div>
                                    )}
                                        </div>
                                    )}
                                </>
                            )}
                            {activeTab === 'notes' && (
                                <>
                                    {!hasPermission(PERMISSIONS.PATIENTS_NOTES_VIEW) ? (
                                        <AccessDenied resource="notes" action="view" />
                                    ) : (
                                        <PatientNotes
                                            patient={patient}
                                            notes={notes}
                                            onNotesChange={loadPatientData}
                                            loading={loading}
                                            currentUser={currentUser}
                                            hasPermission={hasPermission}
                                        />
                                    )}
                                </>
                            )}
                            {activeTab === t.document.pluralLower && (
                                <>
                                    {!hasPermission(PERMISSIONS.PATIENTS_DOCUMENTS_VIEW) ? (
                                        <AccessDenied resource="documents" action="view" />
                                    ) : (
                                        <PatientDocumentsSection 
                                            patient={patient}
                                            currentUser={currentUser}
                                            hasPermission={hasPermission}
                                        />
                                    )}
                                </>
                            )}

                            {/* History Tab */}
                            {activeTab === 'history' && (
                                <PatientHistorySection patientId={patient.patientId} />
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Edit Patient Modal */}
            <PatientEditModal
                isOpen={showEditModal}
                onClose={() => setShowEditModal(false)}
                patient={patient}
                config={config}
                onPatientUpdated={handlePatientUpdated}
            />

            {/* Medication Assignment Modal */}
            <MedicationAssignment
                isOpen={showMedicationAssignment}
                onClose={() => setShowMedicationAssignment(false)}
                patient={patient}
                config={config}
                onMedicationAssigned={handleMedicationAssigned}
            />

            {/* Protocol Assignment Modal */}
            <ProtocolAssignment
                key={selectedProtocol?.patientProtocolId || 'new'}
                isOpen={showProtocolAssignment}
                onClose={() => {
                    setShowProtocolAssignment(false);
                    setSelectedProtocol(null);
                }}
                patient={patient}
                config={config}
                onProtocolAssigned={handleProtocolAssigned}
                existingProtocol={selectedProtocol}
                currentUser={currentUser}
            />

            {/* Protocol Delete Confirmation Modal */}
            {showProtocolDeleteConfirm && protocolToDelete && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-3 bg-red-100 rounded-full">
                                <AlertTriangle className="text-red-600" size={24} />
                            </div>
                            <h3 className="text-xl font-bold text-gray-800">Delete {t.protocol.singular}?</h3>
                        </div>
                        <p className="text-gray-600 mb-4">
                            Are you sure you want to delete <strong>{protocolToDelete.protocolName}</strong>?
                        </p>
                        <p className="text-sm text-gray-600 mb-4">
                            This will permanently remove this {t.protocol.singularLower} and its schedule from {patient.firstName}'s treatment plan.
                        </p>
                        <p className="text-red-600 font-semibold mb-6">This action cannot be undone.</p>
                        <div className="flex gap-3">
                            <button
                                onClick={async () => {
                                    try {
                                        setDeleting(true);
                                        await ApiService.deletePatientProtocol(
                                            patient.patientId,
                                            protocolToDelete.patientProtocolId
                                        );
                                        loadPatientData();
                                        setShowProtocolDeleteConfirm(false);
                                        setProtocolToDelete(null);
                                    } catch (err) {
                                        console.error('Error deleting protocol:', err);
                                        setErrorModal({ title: 'Error Deleting Protocol', message: err.message || 'Failed to delete protocol. Please try again.' });
                                    } finally {
                                        setDeleting(false);
                                    }
                                }}
                                disabled={deleting}
                                className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 disabled:opacity-50 transition flex items-center justify-center gap-2"
                            >
                                {deleting ? (
                                    <>
                                        <Loader className="animate-spin" size={18} />
                                        Deleting...
                                    </>
                                ) : (
                                    <>
                                        <Trash2 size={18} />
                                        Yes, Delete
                                    </>
                                )}
                            </button>
                            <button
                                onClick={() => {
                                    setShowProtocolDeleteConfirm(false);
                                    setProtocolToDelete(null);
                                }}
                                disabled={deleting}
                                className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 disabled:opacity-50 transition"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Service Assignment Modal */}
            <ServiceAssignment
                isOpen={showServiceAssignment}
                onClose={() => setShowServiceAssignment(false)}
                patient={patient}
                config={config}
                onServiceAssigned={handleServiceAssigned}
                currentUser={currentUser}
            />

            {/* Medication Delete Confirmation Modal */}
            {showMedicationDeleteConfirm && medicationToDelete && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-3 bg-red-100 rounded-full">
                                <AlertTriangle className="text-red-600" size={24} />
                            </div>
                            <h3 className="text-xl font-bold text-gray-800">Remove {t.item.singular}?</h3>
                        </div>
                        <p className="text-gray-600 mb-4">
                            Are you sure you want to remove <strong>{medicationToDelete.medicationName}</strong> from {patient.firstName}'s medication list?
                        </p>
                        <p className="text-sm text-gray-600 mb-4">
                            This will unassign this {t.item.singularLower} from the patient but will not delete the {t.item.singularLower} from your library.
                        </p>
                        <p className="text-red-600 font-semibold mb-6">This action cannot be undone.</p>
                        <div className="flex gap-3">
                            <button
                                onClick={async () => {
                                    try {
                                        setDeleting(true);
                                        await ApiService.deletePatientMedication(
                                            patient.patientId,
                                            medicationToDelete.patientMedicationId
                                        );
                                        loadPatientData();
                                        setShowMedicationDeleteConfirm(false);
                                        setMedicationToDelete(null);
                                    } catch (err) {
                                        console.error('Error deleting medication:', err);
                                        setErrorModal({ title: 'Error Deleting Medication', message: err.message || 'Failed to delete medication from patient. Please try again.' });
                                    } finally {
                                        setDeleting(false);
                                    }
                                }}
                                disabled={deleting}
                                className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 disabled:opacity-50 transition flex items-center justify-center gap-2"
                            >
                                {deleting ? (
                                    <>
                                        <Loader className="animate-spin" size={18} />
                                        Removing...
                                    </>
                                ) : (
                                    <>
                                        <Trash2 size={18} />
                                        Yes, Remove
                                    </>
                                )}
                            </button>
                            <button
                                onClick={() => {
                                    setShowMedicationDeleteConfirm(false);
                                    setMedicationToDelete(null);
                                }}
                                disabled={deleting}
                                className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 disabled:opacity-50 transition"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Service Delete Confirmation Modal */}
            {showServiceDeleteConfirm && serviceToDelete && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-3 bg-red-100 rounded-full">
                                <AlertTriangle className="text-red-600" size={24} />
                            </div>
                            <h3 className="text-xl font-bold text-gray-800">Delete {t.service.singular}?</h3>
                        </div>
                        <p className="text-gray-600 mb-4">
                            Are you sure you want to delete <strong>{serviceToDelete.serviceName}</strong> for {patient.firstName}?
                        </p>
                        <p className="text-sm text-gray-600 mb-4">
                            This will remove this scheduled {t.service.singularLower} and its reminder from {patient.firstName}'s calendar.
                        </p>
                        <p className="text-red-600 font-semibold mb-6">This action cannot be undone.</p>
                        <div className="flex gap-3">
                            <button
                                onClick={async () => {
                                    try {
                                        setDeleting(true);
                                        await ApiService.deletePatientService(
                                            patient.patientId,
                                            serviceToDelete.patientServiceId
                                        );
                                        loadPatientData();
                                        setShowServiceDeleteConfirm(false);
                                        setServiceToDelete(null);
                                    } catch (err) {
                                        console.error('Error deleting service:', err);
                                        setErrorModal({ title: 'Error Deleting Service', message: err.message || 'Failed to delete service from patient. Please try again.' });
                                    } finally {
                                        setDeleting(false);
                                    }
                                }}
                                disabled={deleting}
                                className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 disabled:opacity-50 transition flex items-center justify-center gap-2"
                            >
                                {deleting ? (
                                    <>
                                        <Loader className="animate-spin" size={18} />
                                        Deleting...
                                    </>
                                ) : (
                                    <>
                                        <Trash2 size={18} />
                                        Yes, Delete
                                    </>
                                )}
                            </button>
                            <button
                                onClick={() => {
                                    setShowServiceDeleteConfirm(false);
                                    setServiceToDelete(null);
                                }}
                                disabled={deleting}
                                className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 disabled:opacity-50 transition"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Error Modal */}
            {errorModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-3 bg-red-100 rounded-full">
                                <AlertTriangle className="text-red-600" size={24} />
                            </div>
                            <h3 className="text-xl font-bold text-gray-800">{errorModal.title}</h3>
                        </div>
                        <p className="text-gray-600 mb-6">
                            {errorModal.message}
                        </p>
                        <button
                            onClick={() => setErrorModal(null)}
                            className="w-full bg-spruce-600 text-white py-2 px-4 rounded-lg hover:bg-spruce-700 transition"
                        >
                            OK
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PatientDetailPage;